import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environment/environment';
import { IPatient } from '../../interfaces';
import { shakeParams } from '../../classes';
import { IListResponse } from 'src/app/core';

/**
 * Service for managing patient data.
 */
@Injectable({
  providedIn: 'root',
})
export class PatientsService {
  private readonly _http: HttpClient = inject(HttpClient);

  private refetch = new Subject<void>();
  public refetch$ = this.refetch.asObservable();

  private refetchPatientData = new Subject<number>();
  public refetchPatientData$ = this.refetchPatientData.asObservable();

  private search = new Subject<string>();
  public search$ = this.search.asObservable();

  /**
   * Retrieves a list of patients from the API.
   * @param params - The query parameters to include in the request.
   * @returns An Observable that emits an IListResponse of IPatient objects.
   */
  getAll(params: any): Observable<IListResponse<IPatient>> {
    params = shakeParams(params);
    return this._http.get<IListResponse<IPatient>>(
      `${environment.apiGateway}/patient`,
      {
        params,
      }
    );
  }

  /**
   * Retrieves all patients with data for a given doctor.
   * @param doctorId The ID of the doctor to retrieve patients for.
   * @returns An Observable that emits an array of IPatient objects.
   */
  getAllWithData(doctorId: number): Observable<IPatient[]> {
    return this._http.get<IPatient[]>(
      `${environment.apiGateway}/patient/getallpatientwithdata/${doctorId}`
    );
  }

  /**
   * Creates a new patient.
   * @param patient - The patient data to be created.
   * @returns An Observable of the created patient.
   */
  create(patient: FormData): Observable<IPatient> {
    return this._http.post<IPatient>(
      `${environment.apiGateway}/patient`,
      patient
    );
  }

  /**
   * Retrieves a patient by their ID.
   * @param id The ID of the patient to retrieve.
   * @returns An Observable that emits the retrieved patient.
   */
  getById(id: number): Observable<IPatient> {
    return this._http.get<IPatient>(`${environment.apiGateway}/patient/${id}`);
  }

  /**
   * Updates a patient's information.
   * @param patient - The patient's updated information.
   * @returns An Observable that resolves to an unknown value.
   */
  update(patient: FormData): Observable<unknown> {
    return this._http.put<unknown>(
      `${environment.apiGateway}/patient`,
      patient
    );
  }

  /**
   * Deletes a patient with the specified ID.
   * @param id The ID of the patient to delete.
   * @returns An Observable that resolves when the patient is successfully deleted.
   */
  delete(id: number): Observable<void> {
    return this._http.delete<void>(`${environment.apiGateway}/patient/${id}`);
  }

  /**
   * Triggers a refetch of the patients data.
   */
  refetchPatients(): void {
    this.refetch.next();
  }

  /**
   * Triggers a refetch of patient data for the specified patient ID.
   * @param id - The ID of the patient to refetch data for.
   */
  refetchPatient(id: number): void {
    this.refetchPatientData.next(id);
  }

  onSearch(search: string): void {
    this.search.next(search);
  }
}
